import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Dropdown, Modal, Spin, Menu, Button } from 'antd';
import { BookOutlined, RightOutlined } from '@ant-design/icons';

// ── Colors (match HCCReviewConstants.C) ─────────────────────────────────────
const C = {
  navy:      '#162349',
  border:    '#E2E8F0',
  textMuted: '#64748B',
  bg:        '#F9FAFB',
  blue:      '#1E40AF',
  blueBg:    '#EFF6FF',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Match an ICD-10 code prefix to a chapter's code_range.
 * "I48.91" → prefix "I48" → falls in ["I00","I99"] → Chapter 9
 */
function matchCodeToChapter(icdCode, sections) {
  if (!icdCode || !sections) return null;
  const prefix = icdCode.replace(/\./g, '').slice(0, 3).toUpperCase();
  if (prefix.length < 3) return null;

  for (const s of sections) {
    if (!s.code_range) continue;
    const [lo, hi] = s.code_range;
    if (prefix >= lo && prefix <= hi) return s.id;
  }
  return null;
}

/**
 * Build Ant Design Menu items from the guidelines JSON structure.
 */
function buildMenuItems(sections, onSelect, matchedChapterId) {
  // Group: Section I gets special treatment (has blocks + chapters)
  // Sections II–IV + Appendix are simpler (blocks only)
  const sectionNodes = sections.filter(s => s.level === 0);

  return sectionNodes.map(sec => {
    // Children: everything between this section and the next section
    const secIdx = sections.indexOf(sec);
    const nextSecIdx = sections.findIndex((s, i) => i > secIdx && s.level === 0);
    const children = sections.slice(secIdx + 1, nextSecIdx === -1 ? undefined : nextSecIdx);

    const blocks = children.filter(c => c.level === 1);
    const chapters = children.filter(c => c.level === 2);

    // If section has chapters (Section I), build nested structure
    if (chapters.length > 0) {
      const blockItems = blocks
        .filter(b => !b.id.endsWith('Block_C'))  // Block C is just the "Chapter-Specific" label
        .map(b => ({
          key: b.id,
          label: b.title,
          onClick: () => onSelect(b),
        }));

      const chapterItems = chapters.map(ch => {
        const isMatched = ch.id === matchedChapterId;
        const subs = ch.subsections || [];

        if (subs.length === 0) {
          return {
            key: ch.id,
            label: (
              <span style={isMatched ? { fontWeight: 700, color: C.blue } : undefined}>
                {ch.title}
              </span>
            ),
            onClick: () => onSelect(ch),
          };
        }

        return {
          key: ch.id,
          label: (
            <span style={isMatched ? { fontWeight: 700, color: C.blue } : undefined}>
              {ch.title}
            </span>
          ),
          children: [
            {
              key: `${ch.id}__full`,
              label: 'Full chapter text',
              onClick: () => onSelect(ch),
            },
            { type: 'divider' },
            ...subs.map(sub => ({
              key: `${ch.id}.${sub.id}`,
              label: sub.title,
              onClick: () => onSelect({ ...sub, _parentTitle: ch.title }),
            })),
          ],
        };
      });

      return {
        key: sec.id,
        label: sec.title,
        children: [
          ...blockItems,
          ...(blockItems.length > 0 && chapterItems.length > 0 ? [{ type: 'divider' }] : []),
          ...chapterItems,
        ],
      };
    }

    // Simple section (II, III, IV, Appendix): blocks as direct children
    if (blocks.length > 0) {
      return {
        key: sec.id,
        label: sec.title,
        children: [
          {
            key: `${sec.id}__full`,
            label: 'Full section text',
            onClick: () => onSelect(sec),
          },
          { type: 'divider' },
          ...blocks.map(b => ({
            key: b.id,
            label: b.title,
            onClick: () => onSelect(b),
          })),
        ],
      };
    }

    // Leaf section (e.g., Appendix)
    return {
      key: sec.id,
      label: sec.title,
      onClick: () => onSelect(sec),
    };
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// CodingGuidelinesButton
// ─────────────────────────────────────────────────────────────────────────────
const CodingGuidelinesButton = ({ selectedIcdCode }) => {
  const [guidelines, setGuidelines] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);

  // Content modal
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle]     = useState('');
  const [modalContent, setModalContent] = useState('');

  // Lazy-load the JSON on first dropdown open
  const loadGuidelines = useCallback(async () => {
    if (guidelines || loading) return;
    setLoading(true);
    try {
      const resp = await fetch('/guidelines/coding_guidelines.json');
      if (!resp.ok) throw new Error(`${resp.status}`);
      const data = await resp.json();
      setGuidelines(data);
    } catch (err) {
      console.error('Failed to load coding guidelines:', err);
      setError('Could not load guidelines');
    } finally {
      setLoading(false);
    }
  }, [guidelines, loading]);

  // Auto-match selected code to a chapter
  const matchedChapterId = useMemo(() => {
    if (!guidelines?.sections || !selectedIcdCode) return null;
    return matchCodeToChapter(selectedIcdCode, guidelines.sections);
  }, [guidelines, selectedIcdCode]);

  // Handle menu item click → open modal
  const handleSelect = useCallback((item) => {
    const title = item._parentTitle
      ? `${item._parentTitle} › ${item.title}`
      : item.title;
    setModalTitle(title);
    setModalContent(item.content || '(No content available for this section)');
    setModalVisible(true);
  }, []);

  // Build menu items
  const menuItems = useMemo(() => {
    if (!guidelines?.sections) return [];
    return buildMenuItems(guidelines.sections, handleSelect, matchedChapterId);
  }, [guidelines, handleSelect, matchedChapterId]);

  const dropdownContent = loading ? (
    <div style={{ padding: 20, textAlign: 'center', background: 'white', borderRadius: 8 }}>
      <Spin size="small" />
    </div>
  ) : error ? (
    <div style={{ padding: '12px 16px', background: 'white', borderRadius: 8, color: '#DC2626', fontSize: 13 }}>
      {error}
    </div>
  ) : (
    <Menu
      items={menuItems}
      mode="vertical"
      style={{
        maxHeight: '70vh',
        overflowY: 'auto',
        minWidth: 360,
        maxWidth: 480,
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
      }}
    />
  );

  return (
    <>
      <Dropdown
        overlay={dropdownContent}
        trigger={['click']}
        onOpenChange={(open) => { if (open) loadGuidelines(); }}
        placement="bottomRight"
      >
        <Button
          icon={<BookOutlined />}
          data-telemetry-label="coding_guidelines"
          style={{
            background: 'white',
            color: C.navy,
            fontWeight: 600,
            border: 'none',
            fontSize: 16,
          }}
        >
          Coding Guidelines
        </Button>
      </Dropdown>

      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        title={
          <span style={{ fontSize: 16, fontWeight: 700, color: C.navy }}>
            {modalTitle}
          </span>
        }
        footer={null}
        width={780}
        centered
        styles={{
          body: {
            maxHeight: '70vh',
            overflowY: 'auto',
            padding: '16px 24px',
          },
        }}
      >
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12, fontStyle: 'italic' }}>
          ICD-10-CM Official Guidelines for Coding and Reporting FY 2025 — verbatim extract
        </div>
        <pre style={{
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          fontFamily: '"Source Serif 4", Georgia, serif',
          fontSize: 14,
          lineHeight: 1.7,
          color: '#1E293B',
          margin: 0,
          background: 'transparent',
          border: 'none',
          padding: 0,
        }}>
          {modalContent}
        </pre>
      </Modal>
    </>
  );
};

export default CodingGuidelinesButton;